import { isValidWhatsAppTemplatePhoneNumber } from "@multi-chat/shared";
import {
  buildFormTemplatePresentation,
  extractPlaceholderKeys,
  validateTemplateForm,
  type TemplateFormState,
} from "./template-management/template-management-form.js";

// ---------------------------------------------------------------------------
// Template name — Meta-verified rules
// ---------------------------------------------------------------------------

const TEMPLATE_NAME_RE = /^[a-z0-9_]*$/;

export const DEFAULT_TEMPLATE_SUMMARY_NAME = "your_template_name";

/** Strip characters that violate Meta template-name rules (lowercase, digits, underscore). */
export function sanitizeTemplateName(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 512);
}

export function isTemplateNameValid(name: string): boolean {
  return name.length > 0 && name.length <= 512 && TEMPLATE_NAME_RE.test(name);
}

// ---------------------------------------------------------------------------
// Character limits (Meta-verified)
// ---------------------------------------------------------------------------

export const TEMPLATE_LIMITS = {
  name: 512,
  headerText: 60,
  bodyText: 1024,
  footerText: 60,
  /**
   * Cloud API template button label max. WhatsApp Manager UI may show /40;
   * Graph message-template create still documents 25.
   */
  buttonLabel: 25,
  buttonUrl: 2000,
  buttonPhone: 20,
} as const;

/** Voice-call template button TTL (Meta: ttl_minutes 1440–43200). */
export const VOICE_CALL_TTL_MINUTES_MIN = 1440;
export const VOICE_CALL_TTL_MINUTES_MAX = 43200;
export const VOICE_CALL_TTL_MINUTES_DEFAULT = 10080; // 7 days — Manager default
export const VOICE_CALL_DURATION_DAYS_MIN = 1;
export const VOICE_CALL_DURATION_DAYS_MAX = 30;

export const WHATSAPP_CALLING_DOCS_URL =
  "https://developers.facebook.com/documentation/business-messaging/whatsapp/calling";

const NAMED_VAR_TOKEN_RE = /^\{\{[a-z][a-z0-9_]*\}\}$/;
const POSITIONAL_VAR_TOKEN_RE = /^\{\{\d+\}\}$/;

// ---------------------------------------------------------------------------
// Body text formatting (WhatsApp markup)
// ---------------------------------------------------------------------------

export type WhatsAppFormatAction = "bold" | "italic" | "strikethrough" | "monospace";

const FORMAT_MARKERS: Record<WhatsAppFormatAction, { open: string; close: string }> = {
  bold: { open: "*", close: "*" },
  italic: { open: "_", close: "_" },
  strikethrough: { open: "~", close: "~" },
  monospace: { open: "```", close: "```" },
};

export function applyWhatsAppFormat(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  action: WhatsAppFormatAction,
): { value: string; selectionStart: number; selectionEnd: number } {
  const { open, close } = FORMAT_MARKERS[action];
  const before = text.slice(0, selectionStart);
  const selected = text.slice(selectionStart, selectionEnd);
  const after = text.slice(selectionEnd);

  if (selected.length > 0) {
    const value = `${before}${open}${selected}${close}${after}`;
    return {
      value,
      selectionStart: selectionStart + open.length,
      selectionEnd: selectionEnd + open.length,
    };
  }
  const value = `${before}${open}${close}${after}`;
  const caret = selectionStart + open.length;
  return { value, selectionStart: caret, selectionEnd: caret };
}

// ---------------------------------------------------------------------------
// Variable insertion helpers
// ---------------------------------------------------------------------------

export function getNextPositionalIndex(text: string): number {
  const keys = extractPlaceholderKeys(text);
  let max = 0;
  for (const key of keys) {
    const num = parseInt(key, 10);
    if (!isNaN(num) && num > max) max = num;
  }
  return max + 1;
}

// ---------------------------------------------------------------------------
// Button reorder
// ---------------------------------------------------------------------------

export function reorderButtons<T>(
  buttons: readonly T[],
  fromIndex: number,
  toIndex: number,
): T[] {
  const result = [...buttons];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved!);
  return result;
}

export function isQuickReplyButton(button: { type: string }): boolean {
  return button.type === "QUICK_REPLY";
}

export function partitionTemplateButtons<T extends { type: string }>(
  buttons: readonly T[],
): { quickReplies: T[]; ctas: T[] } {
  const quickReplies: T[] = [];
  const ctas: T[] = [];
  for (const button of buttons) {
    if (isQuickReplyButton(button)) quickReplies.push(button);
    else ctas.push(button);
  }
  return { quickReplies, ctas };
}

export function mergePartitionedButtons<T>(
  quickReplies: readonly T[],
  ctas: readonly T[],
): T[] {
  return [...quickReplies, ...ctas];
}

export function createTemplateButtonClientId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `btn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function reorderGroupByClientIds<T extends { type: string; clientId?: string }>(
  buttons: readonly T[],
  group: "QUICK_REPLY" | "CTA",
  nextIds: readonly string[],
): T[] {
  const { quickReplies, ctas } = partitionTemplateButtons(buttons);
  const source = group === "QUICK_REPLY" ? quickReplies : ctas;
  const byId = new Map(
    source.flatMap((button, index) => {
      const id = button.clientId ?? `${group}-${index}`;
      return [[id, button] as const];
    }),
  );
  const used = new Set<string>();
  const nextGroup: T[] = [];
  for (const id of nextIds) {
    const item = byId.get(id);
    if (!item || used.has(id)) continue;
    nextGroup.push(item);
    used.add(id);
  }
  for (const [id, item] of byId) {
    if (!used.has(id)) nextGroup.push(item);
  }

  const isGroupMember =
    group === "QUICK_REPLY"
      ? (button: T) => isQuickReplyButton(button)
      : (button: T) => !isQuickReplyButton(button);

  const result: T[] = [];
  let inserted = false;
  for (const button of buttons) {
    if (!isGroupMember(button)) {
      result.push(button);
      continue;
    }
    if (!inserted) {
      result.push(...nextGroup);
      inserted = true;
    }
  }
  if (!inserted) {
    result.push(...nextGroup);
  }
  return result;
}

export function moveGroupedButton<T extends { type: string; clientId?: string }>(
  buttons: readonly T[],
  clientId: string,
  direction: -1 | 1,
): T[] {
  const target = buttons.find((button) => button.clientId === clientId);
  if (!target) return [...buttons];
  const group = isQuickReplyButton(target) ? "QUICK_REPLY" : "CTA";
  const { quickReplies, ctas } = partitionTemplateButtons(buttons);
  const source = group === "QUICK_REPLY" ? quickReplies : ctas;
  const ids = source.map((button, index) => button.clientId ?? `${group}-${index}`);
  const from = ids.indexOf(clientId);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= ids.length) return [...buttons];
  const nextIds = [...ids];
  const [moved] = nextIds.splice(from, 1);
  nextIds.splice(to, 0, moved!);
  return reorderGroupByClientIds(buttons, group, nextIds);
}

export function duplicateQuickReplyIndexes(
  buttons: readonly { type: string; text: string }[],
): ReadonlySet<number> {
  const byText = new Map<string, number[]>();
  buttons.forEach((button, index) => {
    if (button.type !== "QUICK_REPLY") return;
    const key = button.text.trim().toLowerCase();
    if (!key) return;
    const indexes = byText.get(key) ?? [];
    indexes.push(index);
    byText.set(key, indexes);
  });
  const duplicates = new Set<number>();
  for (const indexes of byText.values()) {
    if (indexes.length < 2) continue;
    for (const index of indexes) duplicates.add(index);
  }
  return duplicates;
}

const TRAILING_URL_VARIABLE_RE = /\{\{\s*[a-zA-Z0-9_]+\s*\}\}$/;

export function isDynamicTemplateUrl(url: string): boolean {
  return extractPlaceholderKeys(url).length > 0;
}

export function toStaticTemplateUrl(url: string): string {
  return url.replace(TRAILING_URL_VARIABLE_RE, "").replace(/\/+$/, "");
}

export function toDynamicTemplateUrl(
  url: string,
  parameterFormat: "NAMED" | "POSITIONAL",
): string {
  if (isDynamicTemplateUrl(url)) return url;
  const token = parameterFormat === "NAMED" ? "{{website_url}}" : "{{1}}";
  const base = url.trim() || "https://www.example.com";
  return `${base.replace(/\/+$/, "")}/${token}`;
}

const LEADING_VARIABLE_RE = /^\{\{\s*[a-zA-Z0-9_]+\s*\}\}/;
const TRAILING_VARIABLE_RE = /\{\{\s*[a-zA-Z0-9_]+\s*\}\}$/;

/**
 * Meta template review: "The message template cannot start or end with a
 * parameter (dangling parameters are not allowed)."
 */
export function bodyHasDanglingVariables(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || extractPlaceholderKeys(trimmed).length === 0) return false;
  return LEADING_VARIABLE_RE.test(trimmed) || TRAILING_VARIABLE_RE.test(trimmed);
}

/**
 * Meta Manager parity for body variable density (empirical, not a published API constant).
 * Whitespace-delimited tokens must satisfy: tokenCount >= (3 * variableOccurrences) + 1.
 * Adjacent text like `t{{3}}` counts as one token.
 */
export function countBodyWhitespaceTokens(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

export function requiredBodyWhitespaceTokens(variableOccurrenceCount: number): number {
  if (variableOccurrenceCount <= 0) return 0;
  return variableOccurrenceCount * 3 + 1;
}

export function bodyHasInsufficientVariableDensity(text: string): boolean {
  const variableCount = extractPlaceholderKeys(text).length;
  if (variableCount === 0) return false;
  return countBodyWhitespaceTokens(text) < requiredBodyWhitespaceTokens(variableCount);
}

/** True when a MARKETING_DEFAULT form contains both call-button types. */
export function hasConflictingCallButtons(
  buttons: TemplateFormState["buttons"],
): boolean {
  let hasPhone = false;
  let hasVoiceCall = false;
  for (const button of buttons) {
    if (button.type === "PHONE_NUMBER") hasPhone = true;
    if (button.type === "VOICE_CALL") hasVoiceCall = true;
    if (hasPhone && hasVoiceCall) return true;
  }
  return false;
}

/**
 * Header supports exactly one variable. Extra `{{...}}` tokens are stripped;
 * surrounding ordinary text is preserved.
 */
export function enforceMaxOneHeaderVariable(text: string): string {
  let seen = 0;
  return text.replace(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g, (match) => {
    seen += 1;
    return seen === 1 ? match : "";
  });
}

export type TemplateVariableFormatStatus =
  | "valid"
  | "named_mismatch"
  | "positional_mismatch"
  | null;

export function templateVariableFormatStatus(
  text: string,
  format: TemplateFormState["parameterFormat"],
): TemplateVariableFormatStatus {
  const keys = extractPlaceholderKeys(text);
  if (keys.length === 0) return null;
  for (const key of keys) {
    const token = `{{${key}}}`;
    if (format === "NAMED" && !NAMED_VAR_TOKEN_RE.test(token)) return "named_mismatch";
    if (format === "POSITIONAL" && !POSITIONAL_VAR_TOKEN_RE.test(token)) {
      return "positional_mismatch";
    }
  }
  return "valid";
}

export function isValidTemplateWebsiteUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  const staticBase = toStaticTemplateUrl(trimmed);
  try {
    const parsed = new URL(staticBase);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export function voiceCallDaysToTtlMinutes(days: number): number {
  const clamped = Math.min(
    VOICE_CALL_DURATION_DAYS_MAX,
    Math.max(VOICE_CALL_DURATION_DAYS_MIN, Math.trunc(days)),
  );
  return clamped * 1440;
}

export function voiceCallTtlMinutesToDays(ttlMinutes: number): number {
  const days = Math.round(ttlMinutes / 1440);
  return Math.min(
    VOICE_CALL_DURATION_DAYS_MAX,
    Math.max(VOICE_CALL_DURATION_DAYS_MIN, days),
  );
}

export function isValidVoiceCallTtlMinutes(ttlMinutes: number): boolean {
  return (
    Number.isInteger(ttlMinutes) &&
    ttlMinutes >= VOICE_CALL_TTL_MINUTES_MIN &&
    ttlMinutes <= VOICE_CALL_TTL_MINUTES_MAX
  );
}

export function buildWhatsAppCallingManagerUrl(input: {
  metaBusinessId: string | null | undefined;
  phoneNumberId: string | null | undefined;
}): string | null {
  const businessId = input.metaBusinessId?.trim();
  const assetId = input.phoneNumberId?.trim();
  if (!businessId || !assetId) return null;
  const url = new URL(
    "https://business.facebook.com/latest/whatsapp_manager/phone_numbers/",
  );
  url.searchParams.set("business_id", businessId);
  url.searchParams.set("asset_id", assetId);
  return url.toString();
}

export function previewShouldCollapseButtons(buttonCount: number): boolean {
  return buttonCount >= PREVIEW_COLLAPSE_THRESHOLD;
}

export function previewVisibleButtonsWhenCollapsed<T>(buttons: readonly T[]): T[] {
  return buttons.slice(0, PREVIEW_VISIBLE_BUTTON_COUNT);
}

export function canSubmitMarketingDefaultTemplate(state: TemplateFormState): boolean {
  if (!isTemplateNameValid(state.name)) return false;
  if (!state.bodyText.trim()) return false;
  if (bodyHasDanglingVariables(state.bodyText)) return false;
  if (bodyHasInsufficientVariableDensity(state.bodyText)) return false;
  if (hasConflictingCallButtons(state.buttons)) return false;
  if (duplicateQuickReplyIndexes(state.buttons).size > 0) return false;

  const headerKeys = state.hasHeader ? extractPlaceholderKeys(state.headerText) : [];
  if (headerKeys.length > 1) return false;
  if (templateVariableFormatStatus(state.headerText, state.parameterFormat)?.endsWith("mismatch")) {
    return false;
  }
  if (templateVariableFormatStatus(state.bodyText, state.parameterFormat)?.endsWith("mismatch")) {
    return false;
  }

  for (const button of state.buttons) {
    if (!button.text.trim()) return false;
    if (button.text.length > TEMPLATE_LIMITS.buttonLabel) return false;
    if (button.type === "URL" && !isValidTemplateWebsiteUrl(button.url)) return false;
    if (button.type === "PHONE_NUMBER" && !isValidWhatsAppTemplatePhoneNumber(button.phoneNumber)) {
      return false;
    }
    if (
      button.type === "VOICE_CALL" &&
      button.ttlMinutes != null &&
      !isValidVoiceCallTtlMinutes(button.ttlMinutes)
    ) {
      return false;
    }
  }

  return validateTemplateForm(state).length === 0;
}

// ---------------------------------------------------------------------------
// Preview: >3 button collapse
// ---------------------------------------------------------------------------

export const PREVIEW_VISIBLE_BUTTON_COUNT = 2;
export const PREVIEW_COLLAPSE_THRESHOLD = 4;

export type TemplateEditorModeKind = "create" | "edit";

export function templateEditorTitleKey(mode: TemplateEditorModeKind): string {
  return mode === "create"
    ? "whatsapp.templates.manage.createTitle"
    : "whatsapp.templates.manage.editTitle";
}

export function templateEditorDescriptionKey(mode: TemplateEditorModeKind): string {
  return mode === "create"
    ? "whatsapp.templates.manage.createDescription"
    : "whatsapp.templates.manage.editDescription";
}

export function templateEditorSubmitLabelKey(mode: TemplateEditorModeKind): string {
  return mode === "create"
    ? "whatsapp.templates.manage.submitCreate"
    : "whatsapp.templates.manage.submitEdit";
}

export function isTemplateEditorNameReadOnly(mode: TemplateEditorModeKind): boolean {
  return mode === "edit";
}

export function isTemplateEditorLanguageReadOnly(mode: TemplateEditorModeKind): boolean {
  return mode === "edit";
}

/** Live preview continues to derive from existing form presentation. No second state layer. */
export function templateEditorPreviewPresentation(state: TemplateFormState) {
  return buildFormTemplatePresentation(state);
}

/** Mobile CSS `order` for the editor grid. Desktop ignores these via `md:order-none`. */
export const templateEditorMobileSectionOrder = {
  basic: 1,
  header: 2,
  body: 3,
  footer: 4,
  examples: 5,
  buttons: 6,
  preview: 7,
} as const;

export const templateEditorMobileOrderClass = {
  basic: "order-1",
  header: "order-2",
  body: "order-3",
  footer: "order-4",
  examples: "order-5",
  buttons: "order-6",
  preview: "order-7",
} as const;

/** Logical alignment for editor Radix selects. Does not restyle the Select surface. */
export const templateEditorSelectTriggerClassName =
  "flex w-full min-w-0 justify-between text-start [&>span]:min-w-0 [&>span]:flex-1 [&>span]:text-start [&>span]:[unicode-bidi:isolate]";

export const templateEditorSelectItemClassName = "w-full text-start";

export const templateEditorSelectContentClassName = "text-start";
